import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../App.css";
import "./HomePage.css";

const hexToRgba = (hex, alpha = 1) => {
  if (!hex) return `rgba(0, 0, 0, ${alpha})`;
  const sanitized = hex.replace("#", "");
  const normalized =
    sanitized.length === 3
      ? sanitized
        .split("")
        .map((char) => char + char)
        .join("")
      : sanitized.padEnd(6, "0");
  const intVal = parseInt(normalized, 16);
  const r = (intVal >> 16) & 255;
  const g = (intVal >> 8) & 255;
  const b = intVal & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const VIEWBOX_WIDTH = 1200;
const VIEWBOX_HEIGHT = 320;
const FILL_STEP = 1.5;
const TICK_MS = 45;

function LoadingScreen({ onComplete }) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let current = 0;
    let hasCompleted = false;
    const interval = setInterval(() => {
      current = Math.min(current + FILL_STEP * 2, 100);
      setProgress(current);
      if (current === 100) {
        clearInterval(interval);
        if (!hasCompleted) {
          hasCompleted = true;
          requestAnimationFrame(() => {
            onComplete();
          });
        }
      }
    }, TICK_MS);

    return () => clearInterval(interval);
  }, [onComplete]);

  const clipHeight = (progress / 100) * VIEWBOX_HEIGHT;
  const clipY = VIEWBOX_HEIGHT - clipHeight;

  return (
    <div className="loading-screen">
      <svg className="loading-logo" viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`} preserveAspectRatio="xMidYMid meet">
        <defs>
          <clipPath id="modelerman-fill">
            <rect x="0" y={clipY} width={VIEWBOX_WIDTH} height={clipHeight} />
          </clipPath>
        </defs>

        <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" className="loading-logo__outline">
          Modelerman
        </text>

        <g clipPath="url(#modelerman-fill)">
          <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" className="loading-logo__fill">
            Modelerman
          </text>
        </g>
      </svg>
    </div>
  );
}

// Stats data
const stats = [
  { value: "50+", label: "Tamamlanan Proje" },
  { value: "8+", label: "Yıllık Deneyim" },
  { value: "100%", label: "Müşteri Memnuniyeti" },
  { value: "4K+", label: "Render Kalitesi" }
];

// Signature projects
const signatureProjects = [
  {
    title: "Harborfront Cultural Pavilion",
    category: "Kültürel Alan",
    image: "https://images.unsplash.com/photo-1505843513577-22bb7d21e455?auto=format&fit=crop&w=800&q=80"
  },
  {
    title: "Lynx Sky Gardens",
    category: "Karma Kullanım",
    image: "https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=800&q=80"
  },
  {
    title: "Alpine Wellness Resort",
    category: "Hospitality",
    image: "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=800&q=80"
  }
];

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [isContentVisible, setIsContentVisible] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const sections = useMemo(() => {
    const translated = t("home.sections", { returnObjects: true });
    return Array.isArray(translated) ? translated : [];
  }, [t, i18n.language]);

  const handleStoreCta = (event, targetUrl = "/store/") => {
    event.preventDefault();
    try {
      window.location.assign(targetUrl);
    } catch (err) {
      console.warn("Store navigation failed", err);
      window.location.href = targetUrl;
    }
  };

  const handleLoadingComplete = () => {
    setIsLoading(false);
  };

  useEffect(() => {
    if (isLoading) {
      setIsContentVisible(false);
    } else {
      setIsContentVisible(true);
    }
  }, [isLoading]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      {isLoading && <LoadingScreen onComplete={handleLoadingComplete} />}

      <div className={`home-page ${isContentVisible ? "home-page--visible" : ""}`}>
        {/* Header */}
        <header className="home-header">
          <Link to="/" className="home-header__logo">
            Modelerman
          </Link>

          {/* Mobile Menu Toggle */}
          <button
            className={`home-header__mobile-toggle ${isMobileMenuOpen ? 'active' : ''}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Menüyü aç/kapat"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          {/* Navigation */}
          <nav className={`home-header__nav ${isMobileMenuOpen ? 'open' : ''}`}>
            <a href="https://portfolio.modelerman.com" target="_blank" rel="noopener noreferrer" className="home-header__link">Portfolyo</a>
            <a href="https://services.modelerman.com" target="_blank" rel="noopener noreferrer" className="home-header__link">Hizmetler</a>
            <a href="/education/" className="home-header__link">Eğitim</a>
            <Link to="/store" className="home-header__link">Mağaza</Link>
            <a href="https://order.modelerman.com" target="_blank" rel="noopener noreferrer" className="home-header__link home-header__link--highlight">Order Panel</a>
            <div className="home-header__actions">
              <Link to="/client/login" className="home-header__btn home-header__btn--outline">
                Müşteri Girişi
              </Link>
              <a href="mailto:studio@modelerman.com" className="home-header__btn home-header__btn--primary">
                İletişim
              </a>
            </div>
          </nav>
        </header>

        {/* Hero Sections */}
        <div className="main-container main-container--visible">
          <div className="sections-container sections-container--immersive">
            {sections.map((section) => {
              const accentSoft = hexToRgba(section.color, 0.55);
              const accentBold = hexToRgba(section.color, 0.78);
              const accentGlow = hexToRgba(section.accent ?? section.color, 0.6);
              const isStoreSection = section.key === "store";

              return (
                <div
                  key={section.key}
                  className={`section section--${section.key}`}
                  style={{
                    "--section-accent": section.color,
                    "--section-accent-soft": accentSoft,
                    "--section-accent-bold": accentBold,
                    "--section-accent-glow": accentGlow,
                  }}
                >
                  <img src={section.img} alt={section.title} className="section__image" />
                  <div className="section__overlay" />
                  <div className="section__content">
                    <div className="section__content-inner">
                      <div className="section__badge brand-wordmark">{section.title}</div>
                      <p className="section__summary">{section.summary}</p>
                      {isStoreSection ? (
                        <a className="section__cta" href={section.url} onClick={(event) => handleStoreCta(event, section.url)}>
                          {section.cta}
                        </a>
                      ) : (
                        <a className="section__cta" href={section.url}>
                          {section.cta}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Stats Section */}
        <section className="home-stats">
          <div className="home-stats__container">
            {stats.map((stat, index) => (
              <div key={index} className="home-stats__item">
                <span className="home-stats__value">{stat.value}</span>
                <span className="home-stats__label">{stat.label}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Signature Projects */}
        <section className="home-signature">
          <div className="home-signature__header">
            <span className="home-signature__badge">Signature Projeler</span>
            <h2 className="home-signature__title">Ödüllü Çalışmalarımız</h2>
            <p className="home-signature__subtitle">Büyük ölçekli masterplan ve karma kullanım projelerinde stüdyo imzamız</p>
          </div>
          <div className="home-signature__grid">
            {signatureProjects.map((project, index) => (
              <article key={index} className="home-signature__card">
                <img src={project.image} alt={project.title} className="home-signature__image" />
                <div className="home-signature__overlay">
                  <span className="home-signature__category">{project.category}</span>
                  <h3 className="home-signature__project-title">{project.title}</h3>
                </div>
              </article>
            ))}
          </div>
          <div className="home-signature__cta">
            <a href="/portfolio/" className="home-signature__btn">
              Tüm Portfolyoyu Gör
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </a>
          </div>
        </section>

        {/* Services Preview */}
        <section className="home-services">
          <div className="home-services__content">
            <span className="home-services__badge">Premium Hizmetler</span>
            <h2 className="home-services__title">End-to-End Görselleştirme</h2>
            <p className="home-services__desc">
              Drone sinematografisinden fotogerçekçi iç mekân render'larına, peyzaj tasarımından
              VR deneyimlerine kadar tüm görselleştirme ihtiyaçlarınız için profesyonel çözümler.
            </p>
            <div className="home-services__list">
              <div className="home-services__item">
                <span className="home-services__icon">🎬</span>
                <span>Drone Sinematografisi</span>
              </div>
              <div className="home-services__item">
                <span className="home-services__icon">🏠</span>
                <span>İç Mekân Görselleştirme</span>
              </div>
              <div className="home-services__item">
                <span className="home-services__icon">🏙️</span>
                <span>Dış Mekân & Kentsel</span>
              </div>
              <div className="home-services__item">
                <span className="home-services__icon">🌳</span>
                <span>Peyzaj & Masterplan</span>
              </div>
            </div>
            <a href="/services/" className="home-services__btn">
              Hizmetleri İncele
            </a>
          </div>
          <div className="home-services__visual">
            <div className="home-services__image-wrapper">
              <img
                src="https://images.unsplash.com/photo-1558655146-d09347e92766?auto=format&fit=crop&w=900&q=80"
                alt="Premium Hizmetler"
                className="home-services__image"
              />
            </div>
          </div>
        </section>

        {/* Store Promo */}
        <section className="home-store">
          <div className="home-store__bg">
            <div className="home-store__glow home-store__glow--1"></div>
            <div className="home-store__glow home-store__glow--2"></div>
          </div>
          <div className="home-store__content">
            <span className="home-store__badge">Modelerman Store</span>
            <h2 className="home-store__title">Premium 3D Asset Ekosistemi</h2>
            <p className="home-store__desc">
              Fotogerçekçi materyal kütüphaneleri, özel STL koleksiyonları ve sınırlı fiziksel drop'lar.
              Texture Lab, Figur Lab ve daha fazlası tek platformda.
            </p>
            <div className="home-store__features">
              <div className="home-store__feature">
                <span className="home-store__feature-icon">🎨</span>
                <span className="home-store__feature-title">Texture Lab</span>
                <span className="home-store__feature-desc">4K PBR materyaller</span>
              </div>
              <div className="home-store__feature">
                <span className="home-store__feature-icon">🗿</span>
                <span className="home-store__feature-title">Figur Lab</span>
                <span className="home-store__feature-desc">Custom figür servisi</span>
              </div>
              <div className="home-store__feature">
                <span className="home-store__feature-icon">📦</span>
                <span className="home-store__feature-title">STL Koleksiyonları</span>
                <span className="home-store__feature-desc">Baskıya hazır modeller</span>
              </div>
            </div>
            <Link to="/store" className="home-store__btn">
              Mağazayı Keşfet
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer className="home-footer">
          <div className="home-footer__top">
            <div className="home-footer__brand">
              <span className="home-footer__logo">Modelerman</span>
              <p className="home-footer__tagline">Premium 3D görselleştirme ve dijital üretim stüdyosu</p>
            </div>
            <div className="home-footer__links">
              <div className="home-footer__col">
                <h4>Hub</h4>
                <a href="/portfolio/">Portfolyo</a>
                <a href="/services/">Hizmetler</a>
                <a href="/education/">Eğitim</a>
                <Link to="/store">Mağaza</Link>
              </div>
              <div className="home-footer__col">
                <h4>Portal</h4>
                <Link to="/client/login">Müşteri Girişi</Link>
                <Link to="/admin/login">Admin Panel</Link>
              </div>
              <div className="home-footer__col">
                <h4>Yasal</h4>
                <Link to="/privacy">Gizlilik Politikası</Link>
              </div>
              <div className="home-footer__col">
                <h4>İletişim</h4>
                <a href="mailto:studio@modelerman.com">studio@modelerman.com</a>
              </div>
            </div>
          </div>
          <div className="home-footer__bottom">
            <p>&copy; {new Date().getFullYear()} Modelerman. Tüm hakları saklıdır.</p>
          </div>
        </footer>
      </div>
    </>
  );
}
